import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { COMPLETION_CTA, TOUR_STEPS } from '../../lib/tourSteps';
import confetti from 'canvas-confetti';
import { CheckCircle, X } from 'lucide-react';

const MODULE_ICONS = {
  super_admin:    ['📊', '👥', '🏢', '📍', '📅', '👷', '📁', '🚨', '🔧', '📋', '⚙️', '👁️', '📦', '❓'],
  lider_proyecto: ['📊', '📁', '📅', '📍', '📋', '📄', '❓'],
  levantador:     ['📊', '📁', '📍', '✅', '🔴', '🗺️', '📱', '❓'],
  cliente:        ['📊', '🚦', '📄', '🗺️', '❓'],
};

export default function CompletionModal() {
  const { user } = useAuth();
  const { showCompletion, setShowCompletion, startTour } = useOnboarding();
  const navigate = useNavigate();
  const fired = useRef(false);

  // Confetti on open
  useEffect(() => {
    if (!showCompletion || fired.current) return;
    fired.current = true;
    const end = Date.now() + 2500;
    const fire = () => {
      confetti({ particleCount: 60, spread: 80, origin: { y: 0.6 }, colors: ['#1A56DB', '#15803D', '#D97706'] });
      if (Date.now() < end) requestAnimationFrame(fire);
    };
    fire();
    return () => { fired.current = false; };
  }, [showCompletion]);

  // ESC closes the modal — must be before any early return so the hook
  // order stays consistent (react-hooks/rules-of-hooks)
  useEffect(() => {
    if (!showCompletion) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowCompletion(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showCompletion, setShowCompletion]);

  if (!showCompletion || !user) return null;

  const role = user.role || 'levantador';
  const cta = COMPLETION_CTA[role] || { label: 'Ir al dashboard', path: '/dashboard' };
  const icons = MODULE_ICONS[role] || [];

  const handleClose = () => setShowCompletion(false);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 animate-fade-in"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-modal-title"
    >
      <div
        className="bg-white rounded-sm shadow-2xl w-full max-w-md text-center relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top-right X button — always visible */}
        <button
          onClick={handleClose}
          aria-label="Cerrar"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-sm bg-white/20 hover:bg-white/40 text-white transition-colors z-10"
          data-testid="completion-close-x"
        >
          <X size={18} />
        </button>

        <div className="bg-gradient-to-r from-soem-primary to-soem-success px-6 py-6 rounded-t-sm">
          <div className="text-5xl mb-3">🎉</div>
          <h2 id="completion-modal-title" className="text-white font-bold text-xl">
            ¡Recorrido listo!
          </h2>
          <p className="text-blue-100 text-sm mt-1">Ya conoces lo básico de SOEM. ¡Es hora de empezar!</p>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* What's next section */}
          <div>
            <p className="text-xs text-slate-400 mb-3 uppercase tracking-wider font-bold">¿Qué sigue?</p>
            <div className="flex flex-wrap justify-center gap-3">
              {icons.slice(0, 8).map((icon, i) => (
                <div key={i} className="relative">
                  <div className="w-12 h-12 bg-green-50 border border-green-200 rounded-sm flex items-center justify-center text-2xl">
                    {icon}
                  </div>
                  <CheckCircle size={14} className="text-green-600 absolute -top-1 -right-1 bg-white rounded-full" />
                </div>
              ))}
            </div>
          </div>

          {/* Primary CTA */}
          <button onClick={() => { setShowCompletion(false); navigate(cta.path); }}
            className="soem-btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="completion-cta-btn">
            {cta.label} →
          </button>

          {/* Secondary actions */}
          <div className="flex items-center justify-center gap-4 text-sm">
            <button onClick={() => { setShowCompletion(false); setTimeout(() => startTour(0), 300); }}
              className="text-slate-400 hover:text-soem-primary transition-colors underline"
              data-testid="restart-tour-link">
              Ver tutorial de nuevo
            </button>
            <span className="text-slate-200">·</span>
            <button onClick={handleClose}
              className="text-slate-500 hover:text-soem-navy transition-colors font-semibold"
              data-testid="completion-close-btn">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
